import { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { PLICHI, SOGGETTO_PRINCIPALE } from "@app/schema";
import { useData } from "../data/DataProvider";
import {
  aggiungiVoci,
  loadModello,
  mandanti,
  propostaDiDefault,
  righeDaModello,
  testoMandanti,
  vociDi,
  type VoceChecklist,
  type VoceModello,
} from "../data/checklist";
import type { Gara, Lotto } from "../data/gare";
import { useAsync } from "../lib/useAsync";
import { describeError } from "./ErrorState";

const useStyles = makeStyles({
  surface: { maxWidth: "820px", width: "100%" },
  form: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM },
  plico: { margin: `${tokens.spacingVerticalS} 0 0`, fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold },
  voci: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", columnGap: tokens.spacingHorizontalM },
  meta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  lotti: { display: "flex", flexWrap: "wrap", columnGap: tokens.spacingHorizontalM },
  barra: { display: "flex", gap: tokens.spacingHorizontalS },
});

/**
 * Preparazione della checklist dal modello (anche per aggiungere voci a una checklist
 * già iniziata: i documenti già presenti non si ripetono). Per le gare a lotti si
 * sceglie a quali lotti applicarla.
 */
export function PreparaChecklistDialog(props: {
  gara: Gara;
  lotti: Lotto[];
  /** Lotto mostrato nella pagina (proposto come destinazione). */
  lotto?: Lotto;
  voci: VoceChecklist[];
  onClose: (saved: boolean) => void;
}) {
  const s = useStyles();
  const data = useData();
  const g = props.gara;
  const modello = useAsync(() => loadModello(data, true), [data]);
  const [rti, setRti] = useState(mandanti(g).join("; "));
  const nomiRti = useMemo(() => testoMandanti(rti.split(/[;\n]/)), [rti]);
  const inRti = !!nomiRti;
  const [scelte, setScelte] = useState<Set<number>>();
  const [destinazioni, setDestinazioni] = useState<Set<number>>(() => new Set(props.lotto ? [props.lotto.id] : []));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();

  // Selezione iniziale: le voci proposte (quelle "solo RTI" se la gara è in RTI)
  const selezione = scelte ?? new Set((modello.data ?? []).filter((m) => propostaDiDefault(m, inRti)).map((m) => m.id));
  const soggetti = [SOGGETTO_PRINCIPALE, ...(nomiRti ? nomiRti.split("; ") : [])];
  const targets: Array<Lotto | undefined> = g.fields.PerLotti ? props.lotti.filter((l) => destinazioni.has(l.id)) : [undefined];

  const scelteModello = (modello.data ?? []).filter((m) => selezione.has(m.id) && (!m.fields.SoloRTI || inRti));
  const daCreare = targets.reduce((n, l) => n + righeDaModello(scelteModello, soggetti, vociDi(props.voci, l)).length, 0);

  const toggle = (m: VoceModello, on: boolean) => {
    const next = new Set(selezione);
    if (on) next.add(m.id);
    else next.delete(m.id);
    setScelte(next);
  };

  async function save() {
    setBusy(true);
    setErr(undefined);
    try {
      if (nomiRti !== (g.fields.Mandanti ?? "")) await data.gare.update(g.id, { Mandanti: nomiRti || null });
      await aggiungiVoci(data, g, targets, (l) => righeDaModello(scelteModello, soggetti, vociDi(props.voci, l)));
      props.onClose(true);
    } catch (e) {
      setErr(describeError(e).detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && props.onClose(false)}>
      <DialogSurface className={s.surface}>
        <DialogBody>
          <DialogTitle>Documenti dal modello</DialogTitle>
          <DialogContent className={s.form}>
            {err && (
              <MessageBar intent="error">
                <MessageBarBody>{err}</MessageBarBody>
              </MessageBar>
            )}
            <Field
              label="Altri componenti RTI"
              hint={`Separati da punto e virgola. Vuoto = ${SOGGETTO_PRINCIPALE} da sola. I documenti "uno per componente" si ripetono per ciascuno.`}
            >
              <Input value={rti} placeholder="es. Cooperativa Rossi; Consorzio Verdi" onChange={(_, d) => setRti(d.value)} />
            </Field>

            {g.fields.PerLotti && (
              <Field label="Lotti" required>
                <div className={s.lotti}>
                  {props.lotti.map((l) => (
                    <Checkbox
                      key={l.id}
                      label={`Lotto ${l.fields.Numero}`}
                      checked={destinazioni.has(l.id)}
                      onChange={(_, d) => {
                        const next = new Set(destinazioni);
                        if (d.checked) next.add(l.id);
                        else next.delete(l.id);
                        setDestinazioni(next);
                      }}
                    />
                  ))}
                  <Button size="small" appearance="transparent" onClick={() => setDestinazioni(new Set(props.lotti.map((l) => l.id)))}>
                    Tutti
                  </Button>
                </div>
              </Field>
            )}

            {modello.error ? (
              <MessageBar intent="error">
                <MessageBarBody>{describeError(modello.error).detail}</MessageBarBody>
              </MessageBar>
            ) : !modello.data ? (
              <Spinner size="small" />
            ) : modello.data.length === 0 ? (
              <span className={s.meta}>Il modello della checklist è vuoto: i documenti si possono aggiungere a mano.</span>
            ) : (
              <>
                <div className={s.barra}>
                  <Button size="small" onClick={() => setScelte(new Set(modello.data!.map((m) => m.id)))}>
                    Seleziona tutti
                  </Button>
                  <Button size="small" onClick={() => setScelte(new Set())}>
                    Nessuno
                  </Button>
                </div>
                {PLICHI.map((p) => {
                  const voci = modello.data!.filter((m) => m.fields.Plico === p);
                  if (!voci.length) return null;
                  return (
                    <div key={p}>
                      <h3 className={s.plico}>{p}</h3>
                      <div className={s.voci}>
                        {voci.map((m) => {
                          const note = [m.fields.PerComponente && inRti && "uno per componente", m.fields.SoloRTI && "solo RTI"].filter(Boolean).join(", ");
                          return (
                            <Checkbox
                              key={m.id}
                              disabled={!!m.fields.SoloRTI && !inRti}
                              checked={selezione.has(m.id) && (!m.fields.SoloRTI || inRti)}
                              onChange={(_, d) => toggle(m, !!d.checked)}
                              label={
                                <>
                                  {m.fields.Title}
                                  {note && <span className={s.meta}> · {note}</span>}
                                </>
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <span className={s.meta} style={{ marginRight: "auto" }}>
              {g.fields.PerLotti && !targets.length
                ? "Scegli almeno un lotto."
                : daCreare === 0
                  ? "Nessun documento nuovo da aggiungere."
                  : `${daCreare} ${daCreare === 1 ? "documento" : "documenti"} da aggiungere${targets.length > 1 ? ` in ${targets.length} lotti` : ""}`}
            </span>
            <Button onClick={() => props.onClose(false)} disabled={busy}>
              Annulla
            </Button>
            <Button appearance="primary" onClick={save} disabled={busy || (daCreare === 0 && nomiRti === (g.fields.Mandanti ?? ""))}>
              {busy ? "Salvataggio…" : "Aggiungi"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
